import React, { useReducer, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * @typedef {Object} Dialog
 * @property {string|null} title
 * @property {React.ReactNode|null} children
 * @property {React.ReactNode|undefined|null} [topRightContent]
 * @property {React.ReactNode|undefined|null} [footer]
 * @property {function} onClose
 */

/**
 * @typedef {Object} Action
 * @property {'SHOW_DIALOG'|'HIDE_DIALOG'} type
 * @property {Dialog} [payload]
 */

const DialogStateContext = React.createContext(null);
const DialogDispatchContext = React.createContext(null);

const initState = {
  isOpen: false,
  content: {
    title: null,
    children: null,
    topRightContent: null,
    footer: null,
    onClose: () => undefined,
  },
};

/**
 * Reducer to manager dialog state
 *
 * @param {Object} state - from initState
 * @param {Action} action
 * @returns {Object} - The new dialog state
 * */
const dialogReducer = (state, action) => {
  switch (action.type) {
    case 'SHOW_DIALOG': {
      try {
        const { title, children } = action.payload;

        if (title && children) {
          return { isOpen: true, content: action.payload };
        }

        return initState;
      } catch (error) {
        throw new Error('useDialog: action \'SHOW_DIALOG\' payload should have title and children property');
      }
    }
    case 'HIDE_DIALOG': {
      return initState;
    }

    default: {
      throw new Error(`useDialog : Unhandled action type: ${action.type}`);
    }
  }
};

export function DialogProvider({ children }) {
  const [state, dispatch] = useReducer(dialogReducer, initState);
  const contextValue = useMemo(() => ({ setDialog: dispatch }), [dispatch]);

  return (
    <DialogStateContext.Provider value={state}>
      <DialogDispatchContext.Provider value={contextValue}>
        {children}
      </DialogDispatchContext.Provider>
    </DialogStateContext.Provider>
  );
}

DialogProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Custom React hook for accessing and manipulating dialog state within a component.
 *
 * @returns {{
 * isOpen:boolean,
 * dialog:Dialog,
 * setDialog:function(Action)}}
 */
export const useDialog = () => {
  const { setDialog } = useContext(DialogDispatchContext);
  const state = useContext(DialogStateContext);

  return {
    isOpen: state.isOpen,
    dialog: state.content,
    setDialog,
  };
};
